import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BrokersSection from "../components/BrokersSection";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import TestimonialsCarousel from "../components/TestimonialsCarousel";

import Slider from "react-slick";
import {
  FaHeartbeat,
  FaPills,
  FaShieldAlt,
  FaHeart,
  FaStar,
  FaQuoteLeft,
  FaHeadset,
  FaUserCheck,
} from "react-icons/fa";
import { useToast } from "../context/ToastContext";

export default function Home() {
  const [scroll, setScroll] = useState(0);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  // ZIP state + verification
  const [zip, setZip] = useState("");
  const [zipStatus, setZipStatus] = useState("idle"); // idle | checking | valid | invalid
  const [place, setPlace] = useState(null); // { city, abbr }
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // ---- Effects ----
  useEffect(() => {
    const handleScroll = () => {
      const top = window.scrollY;
      const height = document.body.scrollHeight - window.innerHeight;
      const percent = Math.min(100, Math.max(0, (top / height) * 100));
      setScroll(percent);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prefill ZIP from storage if available
  useEffect(() => {
    const z = localStorage.getItem("userZipCode");
    if (z && /^\d{5}$/.test(z)) setZip(z);
  }, []);

  // Debounced ZIP verification with zippopotam.us
  useEffect(() => {
    setPlace(null);
    if (!/^\d{5}$/.test(zip)) {
      setZipStatus("idle");
      return;
    }

    const t = setTimeout(async () => {
      setZipStatus("checking");
      try {
        abortRef.current?.abort?.();
        abortRef.current = new AbortController();
        const res = await fetch(`https://api.zippopotam.us/us/${zip}`, {
          signal: abortRef.current.signal,
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        const p = data?.places?.[0];
        if (!p) throw new Error();
        setPlace({ city: p["place name"], abbr: p["state abbreviation"] });
        setZipStatus("valid");
      } catch (e) {
        if (e?.name === "AbortError") return;
        setZipStatus("invalid");
        setPlace(null);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [zip]);

  // ---- Data ----
  const testimonialLogos = [
    "/images/MVP.png",
    "/images/EH.png",
    "/images/UHC.png",
    "/images/LMI.png",
    "/images/HM.png",
    "/images/CG.png",
    "/images/EDP.png",
    "/images/A.png",
    "/images/MP.png",
  ];

  // ---- Handlers ----
  const handleZipSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!/^\d{5}$/.test(zip)) {
      addToast("Please enter a full 5‑digit ZIP code.", "warning");
      return;
    }
    if (zipStatus !== "valid") {
      addToast("Please enter a valid ZIP (we couldn’t verify that one).", "warning");
      return;
    }

    try {
      setLoading(true);
      localStorage.setItem("userZipCode", zip);
      addToast(`Finding plans for ${zip}…`, "success");
      await new Promise((r) => setTimeout(r, 350));
      navigate("/plans");
    } catch {
      addToast("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---- Reusable styles ----
  const colors = {
    navy: "#002b5c",
    red: "#e31c3d",
    accent: "#0e5aa7",
  };

  const sectionTitle = "text-4xl font-extrabold tracking-tight text-[#002b5c]";
  const sectionSub = "text-gray-600 max-w-3xl mx-auto mt-3 text-base md:text-lg";
  const cardBase =
    "group bg-white border border-[#002b5c]/10 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-transform duration-300 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-[#002b5c]/20";

  // ---- Anim variants ----
  const fadeUp = (d = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, delay: d },
  });

  return (
    <div className="overflow-y-auto scrollbar-hide font-sans text-[#002b5c] bg-white">
      {/* Inline utilities so you don’t need global edits */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar{ display:none; }
        .scrollbar-hide{ -ms-overflow-style:none; scrollbar-width:none; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 22s linear infinite; }
      `}</style>

      <Helmet>
        <title>Your Choice Associates | Medicare & Life Insurance</title>
        <meta
          name="description"
          content="Explore Medicare Advantage, Part D Plans, Life Insurance, and more with Your Choice Associates. Trusted by 1000+ families."
        />
        <link rel="canonical" href="https://yourchoiceassociates.org/" />
      </Helmet>

      <Navbar />

      {/* Scroll Progress */}
      <div className="fixed top-0 left-0 w-full h-1 z-[9999] bg-[#e31c3d]/20">
        <div
          className="h-full bg-gradient-to-r from-[#e31c3d] to-[#002b5c]"
          style={{ width: `${scroll}%`, transition: "width 0.2s ease-out" }}
        />
      </div>

      {/* Explore Plans & Coverage Options */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp(0)} className={sectionTitle + " text-center"}>
            Explore Our Plans & Coverage Options
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub + " text-center mt-2"}>
            From Medicare to Life Insurance, we offer a curated range of plans from trusted carriers to meet your health
            and financial goals. Discover what fits your lifestyle best.
          </motion.p>

          <div className="mt-12 grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Medicare Advantage */}
            <motion.div {...fadeUp(0.1)} className={cardBase + " h-full"}>
              <div className="mb-4">
                <FaHeartbeat className="text-3xl text-[#e31c3d]" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#002b5c] leading-snug">
                Medicare<br />Advantage
              </h3>
              <p className="text-gray-600 mt-3">
                All‑in‑one plans combining hospital, medical, and prescription coverage with additional perks like vision
                and dental.
              </p>
              <Link to="/medicare-a" className="inline-flex items-center gap-2 text-[#e31c3d] font-semibold mt-6 group-hover:underline">
                Learn More <span aria-hidden>→</span>
              </Link>
            </motion.div>

            {/* Part D Drug Plans */}
            <motion.div {...fadeUp(0.15)} className={cardBase + " h-full"}>
              <div className="mb-4">
                <FaPills className="text-3xl text-[#e31c3d]" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#002b5c] leading-snug">Part D Drug Plans</h3>
              <p className="text-gray-600 mt-3">
                Affordable prescription drug coverage that complements your existing Medicare plan and reduces out‑of‑pocket costs.
              </p>
              <Link to="/medicare-d" className="inline-flex items-center gap-2 text-[#e31c3d] font-semibold mt-6 group-hover:underline">
                Learn More <span aria-hidden>→</span>
              </Link>
            </motion.div>

            {/* Medicare Supplements */}
            <motion.div {...fadeUp(0.2)} className={cardBase + " h-full"}>
              <div className="mb-4">
                <FaShieldAlt className="text-3xl text-[#e31c3d]" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#002b5c] leading-snug">
                Medicare<br />Supplements
              </h3>
              <p className="text-gray-600 mt-3">
                Supplemental insurance (Medigap) to cover costs not included in Original Medicare like copays and deductibles.
              </p>
              <Link to="/medicare-supplements" className="inline-flex items-center gap-2 text-[#e31c3d] font-semibold mt-6 group-hover:underline">
                Learn More <span aria-hidden>→</span>
              </Link>
            </motion.div>

            {/* Life Insurance */}
            <motion.div {...fadeUp(0.25)} className={cardBase + " h-full"}>
              <div className="mb-4">
                <FaHeart className="text-3xl text-[#e31c3d]" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#002b5c] leading-snug">Life Insurance</h3>
              <p className="text-gray-600 mt-3">
                Secure your family’s future with flexible term and whole life policies tailored to your needs and budget.
              </p>
              <Link to="/life-insurance" className="inline-flex items-center gap-2 text-[#e31c3d] font-semibold mt-6 group-hover:underline">
                Learn More <span aria-hidden>→</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>




      {/* HERO */}
      <section className="relative">
        {/* Background */}
        <img
          src="/images/hero.png"
          alt="Smiling seniors outdoors"
          className="w-full h-[680px] md:h-[720px] object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#002b5c]/80" />

        {/* Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <motion.h1
                {...fadeUp(0)}
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.1] md:leading-[1.05] tracking-tight"
              >
                Find a Plan That Fits You
              </motion.h1>

              <motion.p
                {...fadeUp(0.12)}
                className="mt-4 text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto"
              >
                Start by entering your ZIP code to browse Medicare and Life Insurance plans available in your area.
              </motion.p>

              {/* Input Group */}
              <motion.form onSubmit={handleZipSubmit} {...fadeUp(0.24)} className="mt-8">
                <div className="mx-auto max-w-2xl" aria-busy={loading}>
                  <div
                    className={`relative flex items-stretch bg-white/95 backdrop-blur-sm rounded-full shadow-lg ring-1 overflow-hidden
                    ${zipStatus === "valid" ? "ring-emerald-300" : zipStatus === "invalid" ? "ring-rose-300" : "ring-black/5"}
                    focus-within:ring-2 focus-within:ring-[#0B5EFD]/30`}
                  >
                    {/* Left pin icon */}
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#e31c3d]">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                      </svg>
                    </span>

                    <input
                      type="text"
                      name="zip"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="Enter ZIP Code"
                      maxLength={5}
                      disabled={loading}
                      aria-label="ZIP Code"
                      aria-invalid={zipStatus === "invalid"}
                      className="flex-1 min-w-0 pl-12 pr-4 py-4 text-[#002b5c] font-semibold placeholder:text-[#002b5c]/50 focus:outline-none disabled:opacity-60"
                      value={zip}
                      onChange={(e) => {
                        const v = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                        setZip(v);
                      }}
                    />

                    <button
                      type="submit"
                      disabled={loading || !/^\d{5}$/.test(zip) || zipStatus !== "valid"}
                      className="px-6 sm:px-8 py-4 bg-gradient-to-br from-[#ff5f84] to-[#e31c3d] text-white font-semibold hover:brightness-110 active:scale-[.98] transition disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Finding…
                        </span>
                      ) : (
                        "Get a free quote"
                      )}
                    </button>
                  </div>

                  {/* Helper line (no chunky checkmark) */}
                  <div className="mt-2 min-h-[1.25rem] text-sm" aria-live="polite">
                    {zipStatus === "checking" && (
                      <span className="text-white/80">Checking ZIP…</span>
                    )}
                    {zipStatus === "valid" && place && (
                      <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50/90 px-3 py-1 text-emerald-800">
                        <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                        {place.city}, {place.abbr}
                      </span>
                    )}
                    {zipStatus === "invalid" && (
                      <span className="text-rose-200">We couldn’t verify that ZIP. Please check and try again.</span>
                    )}
                  </div>
                </div>
              </motion.form>

              {/* Trust badge */}
              <motion.div
                {...fadeUp(0.36)}
                className="mt-6 inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white rounded-full px-4 sm:px-5 py-2 shadow-md"
              >
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-yellow-400/90 text-[#002b5c] text-sm font-bold">
                  ★
                </span>
                <span className="text-sm sm:text-base font-medium">
                  Trusted by over <span className="font-bold">10,000+ families</span> nationwide
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

            {/* Trusted Carriers - Auto Scroll */}
      <section className="bg-[#f9fafb] py-16 px-6 border-y border-[#002b5c]/10 overflow-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2 {...fadeUp(0)} className={sectionTitle + " text-center"}>
            We Work With Trusted Carriers
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub + " text-center mt-2 mb-10"}>
            Our team partners with top‑rated insurance providers to ensure the best coverage for every client.
          </motion.p>

          <div className="relative w-full overflow-hidden">
            {/* soft fades on sides */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#f9fafb] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#f9fafb] to-transparent" />

            <div className="flex gap-16 px-6 animate-marquee w-[200%]">
              {[...testimonialLogos, ...testimonialLogos].map((logo, i) => (
                <img
                  key={i}
                  src={logo}
                  alt={`Carrier Logo ${i + 1}`}
                  className="h-12 w-auto opacity-85 hover:opacity-100 hover:scale-105 transition"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      <section id="about" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-8 py-14 sm:py-16 md:py-20 grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-5 sm:space-y-6"
          >
            {/* Chip */}
            <span className="inline-block text-[10px] sm:text-xs font-semibold tracking-wider uppercase text-[#002B5C] bg-[#e8f1fb] px-3 py-1 rounded-full">
              Your Choice Associates
            </span>

            {/* Title */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#002B5C] leading-tight">
              Medicare & Life Insurance Guidance, Done Right
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              For over <strong>20 years</strong>, our licensed advisors have helped individuals and families compare
              Medicare options, Part D drug plans, Medicare Supplement policies, and life insurance with
              <strong> zero pressure</strong> and <strong>clear, agent‑first advice</strong>. We’re a team dedicated to
              doing what’s best for every client, every time.
            </p>

            {/* Trust badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="rounded-2xl border border-gray-200 p-4 sm:p-5">
                <FaShieldAlt className="text-xl sm:text-2xl mb-3 text-[#002B5C]" />
                <h3 className="font-semibold text-[#002B5C]">Licensed & Compliant</h3>
                <p className="text-sm text-gray-600">Guidance that puts you first.</p>
              </div>
              <div className="rounded-2xl border border-gray-200 p-4 sm:p-5">
                <FaHeadset className="text-xl sm:text-2xl mb-3 text-[#002B5C]" />
                <h3 className="font-semibold text-[#002B5C]">Personal Support</h3>
                <p className="text-sm text-gray-600">Real people. Real answers.</p>
              </div>
              <div className="rounded-2xl border border-gray-200 p-4 sm:p-5">
                <FaUserCheck className="text-xl sm:text-2xl mb-3 text-[#002B5C]" />
                <h3 className="font-semibold text-[#002B5C]">No‑Obligation Help</h3>
                <p className="text-sm text-gray-600">Compare before you decide.</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-4">
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#002B5C]">20+</p>
                <p className="text-xs sm:text-sm text-gray-600">Years guiding clients</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#002B5C]">10,000+</p>
                <p className="text-xs sm:text-sm text-gray-600">Families served</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#002B5C]">5★</p>
                <p className="text-xs sm:text-sm text-gray-600">Client satisfaction</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Link
                to="/about"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-md bg-[#002B5C] text-white px-5 py-3 font-semibold shadow hover:bg-[#001d40] transition"
              >
                Learn More About Us
              </Link>
              <Link
                to="/contact"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-md border border-[#002B5C] text-[#002B5C] px-5 py-3 font-semibold hover:bg-[#e8f1fb] transition"
              >
                Talk to an Agent
              </Link>
            </div>
          </motion.div>

          {/* Right: Corporate imagery (collage) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="rounded-2xl overflow-hidden shadow ring-1 ring-black/5">
                <img
                  src="/images/office-1.jpg"
                  alt="Your Choice Associates office"
                  className="w-full h-44 sm:h-48 md:h-56 lg:h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow ring-1 ring-black/5">
                <img
                  src="/images/meeting-1.jpg"
                  alt="Advisors collaborating"
                  className="w-full h-44 sm:h-48 md:h-56 lg:h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow ring-1 ring-black/5">
                <img
                  src="/images/support-1.jpg"
                  alt="Client support"
                  className="w-full h-44 sm:h-48 md:h-56 lg:h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow ring-1 ring-black/5">
                <img
                  src="/images/office-1.webp"
                  alt="Corporate workspace"
                  className="w-full h-44 sm:h-48 md:h-56 lg:h-64 object-cover"
                />
              </div>
            </div>

            {/* Badge: becomes static on mobile, floats on md+ */}
            <div className="mt-4 md:mt-0 md:absolute md:-bottom-6 md:-right-6 bg-white rounded-2xl shadow-lg p-4 w-full sm:w-[260px] border border-gray-100">
              <p className="text-sm text-gray-600">Nationwide Team</p>
              <p className="font-bold text-[#002B5C]">Licensed Advisors</p>
              <p className="text-xs text-gray-500">Local expertise. Corporate standards.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}

      <TestimonialsCarousel />

      {/* BROKERS */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp(0)} className={sectionTitle + " text-center"}>
            Meet Local, Licensed Brokers
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub + " text-center mt-2"}>
            Connect with a specialist near you for no‑cost advice and plan comparisons.
          </motion.p>

          <div className="mt-12">
            {/* If your BrokersSection has its own slider, keep continuous settings there to avoid bounce */}
            <BrokersSection />
          </div>

          <motion.div {...fadeUp(0.2)} className="mt-10 flex justify-center">
            <Link
              to="/brokers"
              className="inline-flex items-center rounded-xl bg-white border border-gray-200 px-5 py-3 font-semibold text-[#002B5C] hover:bg-gray-50 transition"
            >
              View All Brokers
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
